import { faClone, faPencil, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { AdTagCreative, Campaign, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import type { ICreativeCardMenuItem, IUsePoofResult } from '@feathr/components';
import { ConfirmModal, CreativeCard, PoofGridItem, renderErrorsTooltip } from '@feathr/components';
import AddCreativeButton from '@feathr/extender/components/Creatives/AddCreativeButton';
import BannersnackEditorModal from '@feathr/extender/components/Creatives/BannersnackEditorModal';
import CreativeDefaultSettings from '@feathr/extender/components/Creatives/CreativeDefaultSettings';
import CreativeModal from '@feathr/extender/components/Creatives/CreativeModal';
import type { IThumbnailCarouselItem } from '@feathr/extender/components/Creatives/CreativeModal/ThumbnailCarousel';
import { CreativeRequirementsCard } from '@feathr/extender/components/Creatives/CreativeRequirementsCard';
import {
  getCreativeThumbnailUrl,
  hasStoredThumbnail,
} from '@feathr/extender/components/Creatives/Creatives.utils';
import CreativesGrid from '@feathr/extender/components/Creatives/CreativesGrid';

import { useChannelAvailability } from '../../CampaignEditPage.useChannelAvailability';
import { useAdWizardCreativesStep } from './AdWizardCreativesStep.hooks';
import { getCreativeStatusInfo } from './AdWizardCreativesStep.utils';

import * as styles from './AdWizardCreativesStep.css';

interface IProps {
  campaign: Campaign;
  creatives: IObservableArray<DisplayCreative>;
  poof: IUsePoofResult;
}

function AdWizardCreativesStep({ campaign, creatives, poof }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { removingIds, appearingIds, animateAppear } = poof;
  const { availableChannels } = useChannelAvailability({ campaign });

  const {
    isCreativeModalOpen,
    selectedCreative,
    creativeToDelete,
    isBannersnackEditorOpen,
    isBannersnackSyncing,
    toggleCreativeModal,
    handleSelectCreative,
    handleEditCreative,
    handleDuplicateCreative,
    handleDeleteCreative,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditInBannersnack,
    handleBannersnackEditorDone,
    handleBannersnackEditorCancel,
  } = useAdWizardCreativesStep({ campaign, creatives, poof });

  const thumbnailItems: IThumbnailCarouselItem[] = creatives.map((c) => {
    const isAdTag = c.get('_cls') === CreativeClass.DisplayAdTag;
    return {
      id: c.id,
      thumbnailUrl: getCreativeThumbnailUrl(c),
      filename: c.get('name') ?? '',
      isVideo: c.creativeType === 'Video',
      hasThumbnail: hasStoredThumbnail(c),
      isAdTag,
      adTagHtml: isAdTag ? (c as AdTagCreative).get('adtag') : undefined,
      adTagWidth: isAdTag ? c.get('width') : undefined,
      adTagHeight: isAdTag ? c.get('height') : undefined,
    };
  });

  function getMenuItems(creative: DisplayCreative): ICreativeCardMenuItem[] {
    return [
      {
        label: t('Edit'),
        onClick: (): void => handleEditCreative(creative),
        prefix: faPencil,
      },
      {
        label: t('Duplicate'),
        onClick: (): void => handleDuplicateCreative(creative),
        prefix: faClone,
      },
      {
        label: t('Delete'),
        onClick: (): void => handleDeleteCreative(creative),
        prefix: faTrashCan,
        theme: 'danger',
      },
    ];
  }

  return (
    <>
      <Flex className={styles.root} justify={'space-between'}>
        <div className={styles.left}>
          <CreativesGrid
            creatives={creatives}
            emptyStateAction={
              <AddCreativeButton
                availableChannels={availableChannels}
                campaign={campaign}
                campaignId={campaign.id}
                creatives={creatives}
                eventId={campaign.get('event')}
                onAppear={animateAppear}
                type={'primary'}
              />
            }
            renderItem={(creative): JSX.Element => {
              const { status, errors } = getCreativeStatusInfo(creative, campaign, t);

              return (
                <PoofGridItem
                  id={creative.id}
                  isAppearing={appearingIds.has(creative.id)}
                  isRemoving={removingIds.has(creative.id)}
                  key={creative.id}
                >
                  <CreativeCard
                    adTagHtml={creative.get('adtag')}
                    channel={creative.isFacebook ? 'meta' : 'display'}
                    data-name={'creative-card'}
                    filename={creative.get('name') ?? ''}
                    hasThumbnail={hasStoredThumbnail(creative)}
                    isEnabled={creative.get('is_enabled')}
                    menuItems={getMenuItems(creative)}
                    name={creative.name}
                    onEnabledChange={(enabled): void => {
                      runInAction(() => creative.set({ is_enabled: enabled }));
                      creative.patchDirty();
                    }}
                    onImageClick={(): void => handleEditCreative(creative)}
                    size={creative.dimensions}
                    status={status}
                    statusTooltip={renderErrorsTooltip(errors)}
                    thumbnailUrl={getCreativeThumbnailUrl(creative)}
                    type={creative.creativeType}
                  />
                </PoofGridItem>
              );
            }}
          />
          <CreativeDefaultSettings campaign={campaign} />
        </div>
        <div className={styles.right}>
          <CreativeRequirementsCard campaign={campaign} creatives={creatives} />
        </div>
      </Flex>
      <CreativeModal
        campaign={campaign}
        campaignId={campaign.id}
        creative={selectedCreative}
        onClose={toggleCreativeModal}
        onEditInBannersnack={selectedCreative?.isBannersnack ? handleEditInBannersnack : undefined}
        onSelectCreative={handleSelectCreative}
        opened={isCreativeModalOpen}
        thumbnailItems={thumbnailItems}
      />
      {isBannersnackEditorOpen && selectedCreative && (
        <BannersnackEditorModal
          creative={selectedCreative}
          doneDisabled={isBannersnackSyncing}
          doneLabel={t('Done')}
          onCancel={handleBannersnackEditorCancel}
          onDone={handleBannersnackEditorDone}
          opened={true}
        />
      )}
      <ConfirmModal
        confirmButtonText={t('Delete')}
        confirmButtonType={'danger'}
        description={t('Are you sure you want to delete this creative?')}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        opened={!!creativeToDelete}
        t={t}
        title={t('Delete creative: {{name}}', { name: creativeToDelete?.name ?? '' })}
      />
    </>
  );
}

export default observer(AdWizardCreativesStep);
